import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useEntitlements } from "../lib/entitlements";
import LockedFeature from "../components/LockedFeature";
import { Plus, CalendarBlank, Clock, X } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const isoLocalNow = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const formatTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const formatDate = (iso) => new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });

export default function DashSchedule() {
  const { business } = useAuth();
  const ent = useEntitlements();
  const isRestaurant = business.industry === "restaurant";
  const entitled = ent?.entitled?.scheduling;
  const [appts, setAppts] = useState([]);
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [form, setForm] = useState({ client_name: "", phone: "", service: "", starts_at: isoLocalNow(), duration_minutes: 30, notes: "" });

  const load = async () => {
    if (isRestaurant || !entitled) return;
    try { const r = await api.get(`/appointments?date=${date}`); setAppts(r.data); }
    catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, entitled]);

  if (!entitled) {
    return <LockedFeature addonKey="scheduling" name="Scheduling (non-restaurant)" description="For salons, clinics, and retail. Restaurants get our smart waitlist instead — no reservations, no fake bookings, no per-cover fees. $8/month, cancel anytime." />;
  }

  if (isRestaurant) {
    return (
      <div className="max-w-2xl">
        <div className="overline">Scheduling</div>
        <h1 className="text-3xl mb-6">Coming soon for restaurants</h1>
        <div className="paper-card p-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[#E6E2D6] mx-auto flex items-center justify-center mb-4">
            <CalendarBlank size={26} color="#1E3A2F" />
          </div>
          <h2 className="text-2xl mb-2">Restaurants use our smart waitlist instead</h2>
          <p className="text-[#5C6B61] max-w-md mx-auto">We're building a Resy-style booking flow with prepay deposits, party-size floors, and Google integration. For now, focus on the live waitlist and to-go orders.</p>
          <div className="tag tag-notified mt-6 inline-block">On the roadmap</div>
        </div>
      </div>
    );
  }

  const addAppt = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, starts_at: new Date(form.starts_at).toISOString(), duration_minutes: parseInt(form.duration_minutes) };
      await api.post("/appointments", payload);
      setOpen(false); setForm({ client_name: "", phone: "", service: "", starts_at: isoLocalNow(), duration_minutes: 30, notes: "" });
      load(); toast.success("Appointment booked");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cancel = async (id) => {
    await api.delete(`/appointments/${id}`);
    load(); toast.success("Cancelled");
  };

  const setStatus = async (id, status) => {
    await api.patch(`/appointments/${id}`, { status });
    load();
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Bookings</div>
          <h1 className="text-3xl">Schedule</h1>
          <p className="text-[#5C6B61] mt-1">{appts.length} appointments on {formatDate(date + "T00:00")}</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-field !py-2 !w-auto" data-testid="schedule-date" />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary" data-testid="schedule-add-btn"><Plus size={16} color="#fff" /> New appointment</button>
            </DialogTrigger>
            <DialogContent className="bg-white border border-[#E1DFD5]">
              <DialogHeader><DialogTitle>Book appointment</DialogTitle></DialogHeader>
              <form onSubmit={addAppt} className="space-y-3 pt-2">
                <input required className="input-field" placeholder="Client name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="appt-client" />
                <input required className="input-field" placeholder="+15555550100" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="appt-phone" />
                <input className="input-field" placeholder="Service (Haircut, Cleaning, Consult)" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="appt-service" />
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="overline block mb-1">Start</label><input required type="datetime-local" className="input-field" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="appt-starts" /></div>
                  <div><label className="overline block mb-1">Duration (min)</label><input required type="number" min={5} className="input-field" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="appt-duration" /></div>
                </div>
                <input className="input-field" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                <button className="btn-primary w-full justify-center" data-testid="appt-submit">Book & send confirmation</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="paper-card overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-[#E1DFD5] overline">
          <div className="col-span-2">Time</div><div className="col-span-3">Client</div><div className="col-span-3">Service</div><div className="col-span-2">Duration</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {appts.length === 0 && <div className="p-10 text-center text-[#5C6B61]">No appointments this day.</div>}
        {appts.map((a) => (
          <div key={a.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-[#E1DFD5] last:border-0">
            <div className="col-span-2 flex items-center gap-2"><Clock size={14} color="#5C6B61" /><span>{formatTime(a.starts_at)}</span></div>
            <div className="col-span-3"><div className="font-medium">{a.client_name}</div><div className="text-xs text-[#5C6B61]">{a.phone}</div></div>
            <div className="col-span-3 text-sm">{a.service || "—"} {a.notes && <span className="text-[#5C6B61]">· {a.notes}</span>}</div>
            <div className="col-span-2 text-sm">{a.duration_minutes}m</div>
            <div className="col-span-2 flex justify-end gap-2">
              <button onClick={() => setStatus(a.id, "completed")} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid={`appt-complete-${a.id}`}>Done</button>
              <button onClick={() => cancel(a.id)} className="w-8 h-8 rounded-full hover:bg-[#F1D7D2]" data-testid={`appt-cancel-${a.id}`}><X size={14} className="mx-auto" color="#A5382A" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
